import { match } from 'ts-pattern';

import {
  FEOT_ACTIVITY_LOG_FEATURE,
  LAUNCH_ACTIVITY_LOG_MESSAGES,
  OperatorActivityLog,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SURVEY_LAUNCH_EDIT_ACTIVITY_LOG_CATEGORY,
} from '@ps/survey-utils';

import { SurveyLaunchActivityLogTrigger } from '../survey-creation-launch.actions';

export const SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND = {
  launchToggle: 'launchToggle',
  aidaToggled: 'aidaToggled',
  feotStatus: 'feotStatus',
  uniqueBuyerEntryLinksUploaded: 'uniqueBuyerEntryLinksUploaded',
  uniqueBuyerEntryLinksCleared: 'uniqueBuyerEntryLinksCleared',
  emailNotificationChanged: 'emailNotificationChanged',
  selectedProjectManager: 'selectedProjectManager',
  includeExcludeListUploaded: 'includeExcludeListUploaded',
  externalSurveyIdChanged: 'externalSurveyIdChanged',
  surveyGroupingUpdated: 'surveyGroupingUpdated',
  dateRangeFilterApplied: 'dateRangeFilterApplied',
} as const;

export function featureStatus(enabled: boolean): 'ON' | 'OFF' {
  return enabled ? 'ON' : 'OFF';
}

export function launchActivityLogEntry(
  trigger: SurveyLaunchActivityLogTrigger,
): OperatorActivityLog {
  return match(trigger)
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.launchToggle },
      ({ field, enabled }) => ({
        message: LAUNCH_ACTIVITY_LOG_MESSAGES[field],
        // `from` is the state before the click
        metadata: {
          from: featureStatus(!enabled),
          to: featureStatus(enabled),
        },
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.aidaToggled },
      ({ enabled }) => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.aidaToggled,
        metadata: { from: !enabled, to: enabled },
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.feotStatus },
      ({ feot }) => ({
        // Legacy FEOT_STATUS: the whole pacing picture at the moment of
        // the change, from the FEOT toggle and its Midway Survey child.
        message: SURVEY_ACTIVITY_LOG_MESSAGES.feotStatus,
        metadata: {
          feature: FEOT_ACTIVITY_LOG_FEATURE,
          from: featureStatus(feot.from),
          to: featureStatus(feot.to),
          completes: feot.completes,
          kind: feot.pacingKind,
          window: feot.window,
          total: feot.total,
          midway: featureStatus(feot.midway),
          fielded: feot.fielded,
          goal: feot.goal,
          percent: feot.percent,
        },
      }),
    )
    .with(
      {
        kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.uniqueBuyerEntryLinksUploaded,
      },
      () => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.uniqueBuyerEntryLinksUpload,
        category: SURVEY_LAUNCH_EDIT_ACTIVITY_LOG_CATEGORY,
        metadata: {},
      }),
    )
    .with(
      {
        kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.uniqueBuyerEntryLinksCleared,
      },
      () => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.uniqueBuyerEntryLinksClear,
        metadata: {},
      }),
    )
    .with(
      {
        kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.emailNotificationChanged,
      },
      ({ from, to }) => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.emailNotificationChanged,
        metadata: { from, to },
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.selectedProjectManager },
      ({ from, to }) => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.selectedProjectManager,
        metadata: { from, to },
      }),
    )
    .with(
      {
        kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.includeExcludeListUploaded,
      },
      () => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.includeExcludeListUploaded,
        category: SURVEY_LAUNCH_EDIT_ACTIVITY_LOG_CATEGORY,
        metadata: {},
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.externalSurveyIdChanged },
      ({ externalSurveyId }) => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.externalSurveyIdChanged,
        metadata: { externalSurveyId: externalSurveyId || 'empty' },
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.surveyGroupingUpdated },
      () => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.surveyGroupingUpdated,
        metadata: {},
      }),
    )
    .with(
      { kind: SURVEY_LAUNCH_ACTIVITY_LOG_TRIGGER_KIND.dateRangeFilterApplied },
      ({ from, to }) => ({
        message: SURVEY_ACTIVITY_LOG_MESSAGES.dateRangeFilterApplied,
        metadata: { from, to },
      }),
    )
    .exhaustive();
}
